import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core'
import mapboxgl from 'mapbox-gl'
import { Subscription, Subject, fromEvent, merge, of } from 'rxjs'
import { switchMap, debounceTime, first } from 'rxjs/operators'
import { DomSanitizer, Title } from '@angular/platform-browser'

import { AuthenticationService } from '../../services/index'
import { MapComponent } from '../../common/index'
import { ICartoLayerConfig } from '../../common/map/map.component'
import { ContextService } from '../../services/context.service'
import { MapMode } from '../../models/Vertical'
import { MapClickPopupComponent } from '../../common/map-click-popup/map-click-popup.component'
import { MobilityService, TERRUNIT_TYPE } from '../../services/mobility.service'

import { TranslateService } from '@ngx-translate/core'
import { Colors } from '../../common/config/constants'

@Component({
  standalone: false,
  selector: 'app-mobility',
  templateUrl: './mobility.component.html',
  styleUrls: ['./mobility.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobilityComponent implements OnInit, OnDestroy {
  @HostBinding('class.loading') isLoading = false
  @ViewChild('map', { static: false }) map: MapComponent
  @ViewChild('mapClickPopup', { static: false })
  mapClickPopup: MapClickPopupComponent
  @ViewChild('compareSelectfilter', { static: false }) compareSelectfilter

  changeEventComponentState = new Subject<any>()
  isActivedCompareSelector = false
  mapLoaded = false

  get selectedPOI() {
    return this.context.poi
  }

  get selectedPoiRegion() {
    return this.context.poiRegion
  }

  _showPrintView = false

  selectedComparePOI = null
  selectedCompareDelivery = null

  get compareOptions(): any {
    if (this.selectedComparePOI || this.selectedCompareDelivery) {
      return {
        poisToCompare: [this.selectedComparePOI].filter(
          (p) => p !== null && p !== undefined,
        ),
        deliveriesToCompare: [this.selectedCompareDelivery].filter(
          (p) => p !== null && p !== undefined,
        ),
      }
    }
    return null
  }

  get compareLabels() {
    if (this.selectedComparePOI) {
      return [this.currentSelectValue, this.currentCompareValue]
    } else if (this.selectedCompareDelivery) {
      return [
        this.context.getContext().delivery.name,
        this.selectedCompareDelivery.name,
      ]
    }
    return []
  }

  compareMode = false
  reportFullMode = true

  get currentFilter(): any {
    return this.context.getContext()
  }

  get keyIndicator() {
    return this.context.keyIndicator.value
  }

  get keyIndicatorLabel() {
    return this.context.keyIndicator.label
  }

  get currentDelivery() {
    return this.context.vertical.delivery
  }

  get canShowReport() {
    return this.context.canShowReport()
  }

  get isCompareSelectPending() {
    return (
      this.compareSelectfilter &&
      (this.compareSelectfilter.showFilter || this.compareSelectfilter.ready)
    )
  }

  // Greece bbox: [sw_lng, sw_lat, ne_lng, ne_lat]
  mapBbox = [19.37, 34.8, 29.65, 41.75]
  mapCenter: number[] = [23.7, 38.5] // Greece center
  mapZoom = 6

  map3DZoom = 12
  basemapList: any[] = [
    {
      id: 'darkmatter',
      label: 'Dark matter',
      value: '/assets/styles/darkmatter.json',
      active: false,
    },
    {
      id: 'positron',
      label: 'Positron',
      value: '/assets/styles/positron.json',
      active: false,
    },
  ]

  get currentBasemap(): any {
    return this.context && this.context.currentBasemap
  }

  set currentBasemap(val) {
    this.context.currentBasemap = val
  }

  poiList: any[] = []
  currentSelectValue = null
  currentCompareValue = null
  mobilityStats: any = {}
  filteredPoiList: any[] = []

  subscriptions: Subscription = new Subscription()
  brandIcon: any = ''
  isFilterPanelShown = this.context.filterBarOpen

  threeDmode = false
  mapImage = null

  hoverPopupData: {
    top: number
    left: number
    layerId?: string
    show: boolean
    data: any
    primary: boolean
    showFrom: boolean
    tooltip: any
    updated: boolean
  } = {
    top: 0,
    left: 0,
    layerId: '',
    show: false,
    data: null,
    primary: false,
    showFrom: false,
    tooltip: null,
    updated: false,
  }
  poiPopup = null
  poiPopupOrderMap = {
    gender: 1,
    age: 2,
    economic_status: 3,
    visitor_profile: 4,
  }

  generalLayerProperties = {
    id: { type: 'category' },
    name: { type: 'category' },
    region_id: { type: 'category' },
    nvisitors: { type: 'number' },
    nvisitors_other: { type: 'number' },
    nvisitors_density: { type: 'number' },
    nvisitors_density_other: { type: 'number' },
    nvisits: { type: 'number' },
    nvisits_other: { type: 'number' },
    nvisits_density: { type: 'number' },
    nvisits_density_other: { type: 'number' },
    lng: { type: 'number' },
    lat: { type: 'number' },
    ntrips: { type: 'number' },
    tags: { type: 'category' },
    cartodb_id: { type: 'number' },
    delivery_id: { type: 'number' },
    ord: { type: 'number' },
    order: { type: 'number' },
  }
  generalLegendColors = Colors.generalLegend
  generalLayerId = 'generalPolygonLayer'

  poiLayerProperties = {
    id: { type: 'category' },
    name: { type: 'category' },
    region_id: { type: 'category' },
    nvisitors: { type: 'number' },
    nvisitors_other: { type: 'number' },
    nvisitors_density: { type: 'number' },
    nvisitors_density_other: { type: 'number' },
    nvisits: { type: 'number' },
    nvisits_other: { type: 'number' },
    nvisits_density: { type: 'number' },
    nvisits_density_other: { type: 'number' },
    terrunit_src: { type: 'number' },
    tags: { type: 'category' },
    cartodb_id: { type: 'number' },
    ord: { type: 'number' },
    order: { type: 'number' },
  }
  poiLegendColors = Colors.poiLegend
  poiLayerId = 'poiPolygonLayer'

  polygonLayer3DId = 'generalPolygon3DLayer'
  polygonLabelLayerId = 'generalPolygonLabelLayer'

  threeDMarker: any
  beforeLayoutGeneral = 'waterway_label'
  beforeLayoutPoi = 'waterway_label'
  currentCity = ''

  filterPlaceHolder = 'Search a place...'
  comparePlaceHolder = 'Compare to other...'
  showCompareFilterSelector = false
  showStats = true
  legendCompareColorsByDeliverys = Colors.legendTemporalComparison
  legendCompareColorsByPois = Colors.legendSpatialComparison
  compareSteps = [-0.7, -0.4, -0.1, 0.1, 0.4, 0.7]
  generalIdProperty = 'id'

  constructor(
    protected authService: AuthenticationService,
    protected context: ContextService,
    protected sanitizer: DomSanitizer,
    protected changes: ChangeDetectorRef,
    protected mobilityService: MobilityService,
    protected translateService: TranslateService,
    protected titleService: Title,
    protected zone: NgZone,
  ) {}

  ngOnInit() {
    this.isLoading = true
    this.mapLoaded = false
    if (!this.currentBasemap) {
      this.basemapList.forEach((elem) => {
        elem.active = false
      })
      this.currentBasemap = this.basemapList[0]
      this.currentBasemap.active = true
    }
    this.setupBrandIcon()
    this.changeEventComponentState
      .asObservable()
      .pipe(
        debounceTime(100),
        switchMap(() => {
          this.isLoading = true
          this._resetComponent()
          this.changes.markForCheck()
          const promise = Promise.all([
            this._processPoi(),
            this._processStats(),
            this._processMap(),
          ]).then(() => {
            this.isLoading = false
            this.changes.markForCheck()
          })
          return of(promise)
        }),
      )
      .subscribe()

    // none of these subscriptions are called on the component initialization phase
    // just when something in the filter bar changes or when you click the map and select something
    // the one subcription that is called on initalization is the one from onMapLoaded
    this.subscriptions.add(
      merge(
        this.context.filterChanges,
        this.context.poiChanges,
        this.context.deliveriesChanges,
        this.context.densityMode,
      ).subscribe(() => {
        if (this.mapLoaded) {
          this.doComponentChanges()
        } else {
          this.changes.markForCheck()
        }
      }),
    )
  }

  ngOnDestroy() {
    this.map.map.off('mouseout', this.hideHoverPopup)
    if (this.subscriptions) {
      this.subscriptions.unsubscribe()
    }
    this.changes.detach()
    try {
      this.cleanCartoLayers()
    } catch (e) {}
  }

  setupBrandIcon() {
    this.brandIcon = this.sanitizer.bypassSecurityTrustStyle(
      `--opt-brand-icon: url("/assets/img/brands/neighborhood/logo.svg")`,
    )
  }

  getFilterPlaceHolder() {
    const placeholder = this.translateService.instant('Search a place in')
    return `${placeholder} ${this.context.city.name}`
  }

  getDownloadLink() {
    if (!this.context.vertical) {
      return ''
    }
    const id = this.context.vertical.delivery.id
    const { token, user } = this.authService.getUser()
    return ''
  }

  _processMap(): Promise<any> {
    return this.selectedPOI ? this.paintPOIMap() : this.paintGeneralMap()
  }

  _processStats(): Promise<any> {
    const poi = this.selectedPOI || null
    return Promise.all([
      this.mobilityService.getTotalVisitors(poi, this.compareOptions), // tripsSummary
      this.mobilityService.getRanking(
        TERRUNIT_TYPE.MUNICIPALITIES,
        poi,
        this.compareOptions,
      ), // tripsLocal
      this.mobilityService.getRanking(
        TERRUNIT_TYPE.PROVINCES,
        poi,
        this.compareOptions,
      ), // tripsNational
      this.mobilityService.getRanking(
        TERRUNIT_TYPE.COUNTRIES,
        poi,
        this.compareOptions,
      ), // tripsInternational
      this.mobilityService.getTargetStats(poi, null, null, this.compareOptions), // targetStats
      this.mobilityService.getVisitorsAgg(poi, this.compareOptions), // totalVisitors
      this.mobilityService.getPoiStats(poi, this.compareOptions), // poiStats
      this.mobilityService.getRanking(
        TERRUNIT_TYPE.BOROUGH,
        poi,
        this.compareOptions,
      ), // tripsBorough
    ])
      .then((values) => {
        this.mobilityStats = {
          tripsSummary: values[0],
          tripsLocal: values[1],
          tripsNational: values[2],
          tripsInternational: values[3],
          targetStats: values[4],
          visitors: values[5],
          poiStats: values[6],
          tripsBorough: values[7],
          selectedPoi: this.selectedPOI,
        }
      })
      .catch((error) => {
        console.error(error)
      })
  }

  _processPoi(): Promise<any> {
    if (this.canShowSelectFilter() || this.canShowCitySelector()) {
      return this.mobilityService.getTerritoryList().then((data) => {
        this.poiList = data.map((item) => ({
          label: item.name,
          value: item.id,
          region_id: item.region_id.toString(),
          brand: 'neighborhood',
          centroid: [item.lng, item.lat],
        }))
      })
    } else {
      return Promise.resolve()
    }
  }

  _resetPoi() {
    if (this.selectedPOI === null) {
      this.currentSelectValue = ''
      this.currentCompareValue = ''
    } else if (this.isMapGridMode()) {
      this.currentSelectValue = `Grid #${this.selectedPOI}`
    }
  }

  _resetMap() {
    this.map.clearPopups()
  }

  _resetStats() {
    for (const key of Object.keys(this.mobilityStats)) {
      this.mobilityStats[key] = undefined
    }
    this.compareMode = this.compareOptions !== null
  }

  _resetComponent() {
    this._resetMap()
    this._resetStats()
    this._resetPoi()
  }

  canShowSelectFilter() {
    return !this.isLoading && (!this.isMapGridMode() || this.selectedPOI)
  }

  canShowCitySearch() {
    return this.isMapGridMode() && !this.selectedPOI
  }

  canShowCitySelector() {
    return this.context.vertical.territories.length > 1
  }

  changeBasemap(item) {
    try {
      this.cleanCartoLayers()
    } catch (e) {}
    this.currentBasemap = item
    this.initMap().then(() => {
      this.doComponentChanges()
    })
  }

  cleanCartoLayers() {
    this.map.removeCartoLayer(this.generalLayerId)
    this.map.removeCartoLayer(this.poiLayerId)
  }

  onMapLoaded() {
    this.zone.runOutsideAngular(() => {
      this.map.invalidateSize()
      this.map.map.on('mouseout', this.hideHoverPopup)
      this.subscriptions.add(
        this.context.$currentVertical.pipe(first()).subscribe(() => {
          this.map.flyToBbox(this.mapBbox, {
            duration: 0,
            linear: true,
            padding: 150,
          })
          if (this.mapLoaded) {
            this.doComponentChanges()
            return
          }
          this.initMap().then(() => {
            this.doComponentChanges()
          })
        }),
      )
    })
  }

  getTilesUrl(metadata) {
    const tiles = metadata.tilejson.vector.tiles
    return tiles
  }

  setupLayerLegendUpdate(layerId, dueTime = 100) {
    const layer = this.map.getLayer(layerId)
    this.subscriptions.add(
      fromEvent(layer, 'updated')
        .pipe(debounceTime(dueTime))
        .subscribe(() => {
          if (layer.visible) {
            this.onLayerUpdate()
          }
        }),
    )
  }

  getVariableFromFeature(feat, key: string) {
    const val = feat.variables[key]
    return val && val.value
  }

  protected setupPoiPopup(ev) {
    if (this.isCompareSelectPending) {
      return
    }

    if (!this.poiPopup) {
      this.poiPopup = new mapboxgl.Popup({
        maxWidth: 'none',
        closeButton: true,
        closeOnClick: false,
        anchor: 'bottom-left',
        offset: [6, -6],
      })
    }

    const feature = ev.features && ev.features[0]
    if (!feature) {
      return
    }

    this.hideHoverPopup()
    this.poiPopup.setLngLat(ev.coordinates)
    this.mapClickPopup.loading = true
    const selectedTargets = this.currentDelivery.target.subfilters.filter(
      (f) => f.value,
    )

    this.mapClickPopup.tabs = (
      selectedTargets.length === 0
        ? this.currentDelivery.target.subfilters
        : selectedTargets
    )
      .filter((f) => !f.hideTarget)
      .map((s) => ({
        title: s.title,
        key: s.key_name,
        order: this.poiPopupOrderMap[s.key_name],
      }))
      .sort((a, b) => a.order - b.order)

    this.mapClickPopup.data = {
      properties: {
        id: feature.id,
        name: this.getVariableFromFeature(feature, 'name'),
        nvisitors: this.getVariableFromFeature(feature, 'nvisitors'),
        nvisitors_other:
          this.getVariableFromFeature(feature, 'nvisitors_other') || 0,
        nvisits: this.getVariableFromFeature(feature, 'nvisits'),
        nvisits_other:
          this.getVariableFromFeature(feature, 'nvisits_other') || 0,
        terrunit_src: this.getVariableFromFeature(feature, 'terrunit_src'),
      },
    }
    this.poiPopup.setDOMContent(this.mapClickPopup.elRef.nativeElement)
    this.poiPopup.addTo(this.map.map)
    this.poiPopup.component = this.mapClickPopup
    this.updatePoiPopup()
  }

  protected updatePoiPopup() {
    if (!this.poiPopup || !this.poiPopup.component) {
      return
    }
    const component = this.poiPopup.component
    const { id, terrunit_src } = component.data.properties
    this.mobilityService
      .getTargetStats(this.selectedPOI, id, terrunit_src, this.compareOptions)
      .then((stats) => {
        const feature = this.getFeature(id)
        const keys = Object.keys(stats).sort((a, b) => {
          return this.poiPopupOrderMap[a] - this.poiPopupOrderMap[b]
        })
        component.data = {
          properties: {
            ...component.data.properties,
            nvisitors:
              feature && feature.properties && feature.properties.nvisitors,
            nvisitors_other:
              feature &&
              feature.properties &&
              feature.properties.nvisitors_other,
            nvisits:
              feature && feature.properties && feature.properties.nvisits,
            nvisits_other:
              feature && feature.properties && feature.properties.nvisits_other,
          },
        }
        component.selectedStats = this.currentFilter.target
        component.stats = stats
        component.currentTab =
          keys.find((key) => this.currentFilter.target[key]) || keys[0]
        component.loading = false
        component.compareMode = this.compareMode
        component.changeDetectorRef.detectChanges()
      })
  }

  hideHoverPopup = () => {
    this.zone.run(() => {
      this.hoverPopupData.show = false
      this.hoverPopupData.layerId = ''
      // this.changes.markForCheck();
    })
  }

  protected getFeature(id) {
    const features = this.map.getLayer(this.poiLayerId)._viz.variables.features
    if (features) {
      return features.eval().find((f) => f.id === id)
    }
    return null
  }

  getHoverEvents(
    blendDelay = 50,
    useFeatureEnter = true,
  ): Record<string, Function> {
    return {
      featureHover: (ev) => {
        if (useFeatureEnter) {
          this.createHoverPopup(ev)
          ev.features.forEach((feat) => {
            feat.strokeColor.blendTo(
              `opacity(${
                this.isLightBasemap() ? Colors.colorN50 : Colors.colorN100
              }, 1)`,
              blendDelay,
            )
            feat.strokeWidth.blendTo('2', blendDelay)
          })
        }
      },
      featureLeave: (ev) => {
        const isOut = ev.features.find((feat) => {
          return feat.id === this.hoverPopupData.data.id
        })
        if (isOut) {
          ev.features.forEach((feat) => {
            feat.strokeColor.reset()
            feat.strokeWidth.reset()
          })
          this.hideHoverPopup()
        }
      },
    }
  }

  protected initMap() {
    this._resetStats()
    return Promise.all([
      this.getMap(),
      this._processStats(),
      this._processPoi(),
    ])
      .then(async (values) => {
        await this.initLayers(values[0])
        this.mobilityStats.legend = [0, 0]
        this.isLoading = false
        this.mapLoaded = true
        this.changes.markForCheck()
      })
      .catch((error) => {
        console.error(error)
        this.isLoading = false
      })
  }

  protected async initLayers(cartoLayer) {
    if (!this.map.getLayer(this.generalLayerId)) {
      const events = this.getHoverEvents()
      events.featureClick = (ev) => this.onMapClick(ev)
      const generalLayerConfig: ICartoLayerConfig = {
        id: this.generalLayerId,
        source: this.map.createMVTSource({
          url: this.getTilesUrl(cartoLayer.metadata),
          properties: this.generalLayerProperties,
          idProperty: this.generalIdProperty,
        }),
        viz: await this.getGeneralViz(),
        events,
        before: this.beforeLayoutGeneral,
      }
      this.map.addLayer(generalLayerConfig)
      this.setupLayerLegendUpdate(this.generalLayerId)
    }

    if (!this.map.getLayer(this.poiLayerId)) {
      const events = this.getHoverEvents(undefined, true)
      events.featureClick = (ev) => this.setupPoiPopup(ev)
      events.featureClickOut = () => {
        if (this.poiPopup) {
          this.poiPopup.remove()
        }
      }
      const poiLayerConfig: ICartoLayerConfig = {
        id: this.poiLayerId,
        source: this.map.createMVTSource({
          url: this.getTilesUrl(cartoLayer.metadata),
          properties: this.poiLayerProperties,
        }),
        viz: this.getPoiViz(),
        events,
        before: this.beforeLayoutPoi,
      }
      this.map.addLayer(poiLayerConfig)
      this.map.hideCartoLayer(this.poiLayerId)
      this.setupLayerLegendUpdate(this.poiLayerId)
    }
  }

  onLayerUpdate() {
    const layerId = this.selectedPOI ? this.poiLayerId : this.generalLayerId
    const layer = this.map.getLayer(layerId)
    if (!this.mapLoaded || this.isLoading || !layer) {
      return
    }
    this.processLegendLabels(layer._viz)
    this.changes.detectChanges()
  }

  onFilterPanelToggle(panelShown) {
    this.isFilterPanelShown = panelShown
    this.animateMapSizeReset()
  }

  onPOISelected(event) {
    event = event || ''
    this.currentSelectValue = event.label || event.value || ''
    this.context.setPoi(event || null)
    this.selectedComparePOI =
      event.value === undefined ? null : this.selectedComparePOI
    if (event.value === undefined && this.poiPopup) {
      this.poiPopup.remove()
    }

    this.filteredPoiList = this.currentSelectValue
      ? this.poiList.filter((poi) => poi.label !== this.currentSelectValue)
      : this.poiList

    if (!this.isMapGridMode()) {
      const hasComparePoi =
        this.selectedComparePOI !== null &&
        this.selectedComparePOI !== undefined
      if (!hasComparePoi) {
        this.selectedCompareDelivery = null
      }
    }

    this.changes.detectChanges()
    // if (!event) {
    //   this.flyToBbox();
    // }
  }

  onPOICompareSelected(event) {
    event = event || ''
    this.currentCompareValue = event.label || ''
    this.selectedComparePOI = event.value
    if (this.mapLoaded) {
      this.doComponentChanges()
    }
  }

  updateHoverPopup(ev) {
    const popupMargin = 12
    if (
      !this.hoverPopupData ||
      !this.hoverPopupData.tooltip ||
      !this.hoverPopupData.data
    ) {
      this.createHoverPopup(ev)
    } else {
      this.zone.run(() => {
        this.hoverPopupData = {
          ...this.hoverPopupData,
          top: ev.position.y + popupMargin,
          left: ev.position.x + popupMargin,
        }
      })
    }
  }

  createHoverPopup(ev) {
    const popupMargin = 12
    // NOTE: .sort() modifies the original ev.features array
    ev.features.sort((a, b) => {
      const aKey = this.getVariableFromFeature(a, 'ord')
      const bKey = this.getVariableFromFeature(b, 'ord')
      return bKey - aKey
    })
    const feat = ev.features[0]
    if (!feat) {
      return
    }
    const keyIndicatorOptions = this.context.keyIndicatorOptions.slice()
    if (this.context.isDensityActive()) {
      keyIndicatorOptions.forEach((keyIndicator, idx) => {
        keyIndicatorOptions.splice(idx + 1, 0, {
          label: this.translateService.instant(keyIndicator.label) + '/km²',
          value: keyIndicator.value + '_density',
        })
      })
    }
    const nvars = keyIndicatorOptions
      .map((opt) => {
        const key = opt.value
        const value = this.getVariableFromFeature(feat, key)
        return { value, label: opt.label, key }
      })
      .sort((a, b) => Number(b.key === this.context.keyIndicator.value))

    const count = this.getVariableFromFeature(feat, 'count')
    const name = this.getVariableFromFeature(feat, 'name')
    const popupData = {
      id: feat.id,
      keyIndicator: this.keyIndicator,
      name: count > 1 ? `${count} POIs` : name,
      ntrips: this.getVariableFromFeature(feat, 'ntrips') || 0,
      ntrips_other: this.getVariableFromFeature(feat, 'ntrips_other') || 0,
      nvars,
    }
    this.zone.run(() => {
      this.hoverPopupData = {
        top: ev.position.y + popupMargin,
        left: ev.position.x + popupMargin,
        data: popupData,
        show: true,
        layerId: feat.layerId,
        primary: !this.selectedPOI,
        showFrom: this.selectedPOI && this.selectedPOI !== feat.id,
        tooltip: this.currentDelivery.tooltip,
        updated: false,
      }
    })
  }

  getPOIFromFeature(feature) {
    const name = this.getVariableFromFeature(feature, 'name')
    const id = this.getVariableFromFeature(feature, 'id') || feature.id
    return {
      value: id,
      region_id: this.getVariableFromFeature(feature, 'region_id'),
      centroid:
        this.getVariableFromFeature(feature, 'centroid') ||
        feature.getRenderedCentroid(),
      label: this.isMapGridMode() ? `Grid #${feature.id}` : name,
    }
  }

  onMapClick(ev) {
    if (
      this.selectedCompareDelivery ||
      (this.selectedPOI &&
        this.showCompareFilterSelector &&
        !this.isActivedCompareSelector)
    ) {
      this.setupPoiPopup(ev)
      return
    }

    const feature = ev.features[0]
    if (!feature) {
      return
    }

    if (!this.threeDMarker) {
      const el = document.createElement('div')
      el.className = '_3dmarker'
      el.innerHTML += `
        <div class="icon"></div>
        <div class="bar"></div>
        <div class="ball"></div>
      `
      this.threeDMarker = new mapboxgl.Marker(el)
        .setLngLat(ev.coordinates)
        .addTo(this.map.map)
    } else {
      this.threeDMarker.setLngLat(ev.coordinates)
    }

    this.isActivedCompareSelector = false
    const poi = this.getPOIFromFeature(feature)
    if (this.isCompareSelectPending) {
      this.onPOICompareSelected(poi)
    } else {
      this.onPOISelected(poi)
    }
  }

  getAuthorizationToken() {
    return this.authService.getUser().token
  }

  protected animateMapSizeReset() {
    this.zone.runOutsideAngular(() => {
      const frames = 125
      const tpf = 2000 / frames
      for (let i = 0; i < frames; i++) {
        setTimeout(() => {
          this.map.invalidateSize()
        }, tpf * i)
      }
    })
  }

  invalidateMapSize() {
    if (this.map) {
      this.map.invalidateSize()
    }
  }

  protected flyToBbox() {
    this.map.flyToBbox(this.mapBbox, {
      padding: 150,
      pitch: this.threeDmode ? 60 : 0,
      speed: 0.5,
    })
  }

  protected paintGeneralMap() {
    this.isLoading = true
    return this.getMap()
      .then(async (layerData) => {
        this.map.hideCartoLayer(this.poiLayerId)

        const viz = await this.getGeneralViz()
        await this.map.updateVectorTileSource(
          this.generalLayerId,
          this.getTilesUrl(layerData.metadata),
          this.generalLayerProperties,
          viz,
          this.generalIdProperty,
        )

        this.map.showCartoLayer(this.generalLayerId)
        this.map.moveLayer(this.generalLayerId, this.beforeLayoutGeneral)

        this.isLoading = false

        const contextCity = this.context.getContext().city.id
        if (contextCity !== this.currentCity) {
          this.currentCity = contextCity
          this.flyToBbox()
        }
      })
      .catch((error) => {
        console.error(error)
      })
  }

  protected getMap() {
    return this.mobilityService.getMap({ compareOptions: this.compareOptions })
  }

  protected paintPOIMap() {
    this.isLoading = true

    return Promise.all([
      this.mobilityService.getMap({
        selectedPoi: this.selectedPOI,
        compareOptions: this.compareOptions,
      }),
      this.mobilityService.getMap(),
    ])
      .then(async (values) => {
        const poiLayerData = values[0]
        const generalLayerData = values[1]
        const poiTilesURL = this.getTilesUrl(poiLayerData.metadata)

        this.map
          .updateVectorTileSource(
            this.poiLayerId,
            poiTilesURL,
            this.poiLayerProperties,
            this.getPoiViz(),
          )
          .then(() => {
            this.map.showCartoLayer(this.poiLayerId)
            this.map.moveLayer(this.poiLayerId, this.generalLayerId)
            if (this.poiPopup) {
              this.poiPopup.remove()
            }
          })

        // Update 3d layer
        /*
				const scale = minmax.max / 500;
				this.update3DLayer(this.getPoiPolygonLayerStyle(minmax), scale, this.);
				*/

        this.map
          .updateVectorTileSource(
            this.generalLayerId,
            this.getTilesUrl(generalLayerData.metadata),
            this.generalLayerProperties,
            await this.getGeneralViz(),
            this.generalIdProperty,
          )
          .then(() => {
            this.map.moveLayer(this.generalLayerId, this.beforeLayoutGeneral)
          })

        this.isLoading = false
        this.mapLoaded = true
        this.changes.markForCheck()

        if (this.threeDMarker && this.threeDmode) {
          this.threeDMarker.getElement().classList.add('active')
        }

        const contextCity = this.context.getContext().city.id
        if (contextCity !== this.currentCity) {
          this.currentCity = contextCity
          this.flyToBbox()
        }
      })
      .catch((error) => {
        console.error(error)
      })
  }

  getLegendColors() {
    let colors = []
    if (this.compareMode) {
      colors = this.selectedComparePOI
        ? this.legendCompareColorsByPois
        : this.legendCompareColorsByDeliverys
    } else {
      colors = this.selectedPOI
        ? this.poiLegendColors
        : this.generalLegendColors
    }
    return this.isLightBasemap() ? colors.slice().reverse() : colors
  }

  processLegendLabels(viz) {
    if (this.compareMode) {
      this.mobilityStats.legend = this.selectedComparePOI
        ? ['+ Reference', '+ Compared']
        : ['Decrease', 'Maintain', 'Increase']
      this.mobilityStats.legendExtra = this.selectedComparePOI
        ? this.context.mapMode === TERRUNIT_TYPE.MUNICIPALITIES
          ? 'Municipality'
          : 'Store'
        : null
    } else {
      const { max_ntrips, min_ntrips } = viz.variables,
        minmax = {
          max: max_ntrips && max_ntrips.eval(),
          min: min_ntrips && min_ntrips.eval(),
        }
      this.mobilityStats.legend = [minmax.min, minmax.max]
    }
  }

  protected _processGridStatsByPoi() {
    return Promise.all([
      this.mobilityService.getTotalVisitors(
        this.selectedPOI,
        this.compareOptions,
      ), // tripsSummary
      this.mobilityService.getRanking(
        TERRUNIT_TYPE.MUNICIPALITIES,
        this.selectedPOI,
        this.compareOptions,
      ), // tripsLocal
      this.mobilityService.getRanking(
        TERRUNIT_TYPE.PROVINCES,
        this.selectedPOI,
        this.compareOptions,
      ), // tripsNational
      this.mobilityService.getRanking(
        TERRUNIT_TYPE.COUNTRIES,
        this.selectedPOI,
        this.compareOptions,
      ), // tripsInternational
      this.mobilityService.getTargetStats(
        this.selectedPOI,
        null,
        null,
        this.compareOptions,
      ), // targetStats
      this.mobilityService.getVisitorsAgg(
        this.selectedPOI,
        this.compareOptions,
      ), // totalVisitors
    ])
      .then((values) => {
        this.mobilityStats.tripsSummary = values[0]
        this.mobilityStats.tripsLocal = values[1]
        this.mobilityStats.tripsNational = values[2]
        this.mobilityStats.tripsInternational = values[3]
        this.mobilityStats.targetStats = values[4]
        this.mobilityStats.visitors = values[5]

        // this.mobilityStats.legend = values[1][0];
        this.mobilityStats.selectedPOI = this.selectedPOI
      })
      .catch((error) => {
        console.error(error)
      })
  }

  deliveryToCompareSelected(delivery) {
    this.selectedCompareDelivery = delivery

    if (this.mapLoaded) {
      this.doComponentChanges()
    }
  }

  protected doComponentChanges() {
    this.zone.run(() => {
      this.changeEventComponentState.next(this.context.getContext())
    })
  }

  isMapGridMode() {
    return this.currentDelivery.mapMode === MapMode.GRID
  }

  canShowDeliverySelector() {
    return Object.keys(this.context.vertical.deliveries).length > 1
  }

  isLightBasemap() {
    return this.currentBasemap && this.currentBasemap.id === 'positron'
  }

  getVizCommonVariables() {
    if (!this.currentDelivery) {
      return ''
    }

    const varKeys = Object.keys(this.currentDelivery.config.variables)
    const varsViz = varKeys
      .filter((x) => this.generalLayerProperties[x])
      .map((key) => `@${key}: $${key}`)
      .join('\n')
    let keyIndicator = this.keyIndicator
    let densityViz = ''
    if (this.context.isDensityActive()) {
      densityViz = varKeys
        .map((key) => `@${key}_density: $${key}_density`)
        .join('\n')
      keyIndicator += '_density'
    }
    return `
      @name: $name
      @region_id: $region_id
      ${varsViz}
      @ntrips: $${keyIndicator}
      ${densityViz}
    `
  }

  async getGeneralViz() {
    if (this.selectedPOI === null) {
      if (!this.compareMode) {
        let colors = this.generalLegendColors
        if (this.isLightBasemap()) {
          colors = colors.slice().reverse()
        }
        return `
          ${this.getVizCommonVariables()}
          @max_ntrips: viewportMax(@ntrips)
          @min_ntrips: viewportMin(@ntrips)
          @centroid: [$lng, $lat]
          strokeWidth: 0.5
          strokeColor: opacity(${
            this.isLightBasemap() ? Colors.colorN50 : Colors.colorN100
          }, 0.4)
          color: ramp(
            linear(@ntrips, @min_ntrips, @max_ntrips),
            [${colors}]
          )
        `
      } else {
        return this.getVizCompareByDeliveries()
      }
    } else {
      return `
        ${this.getVizCommonVariables()}
        @max_ntrips: viewportMax(@ntrips)
        @min_ntrips: viewportMin(@ntrips)
        strokeWidth: 0.5
        strokeColor: opacity(${
          this.isLightBasemap() ? Colors.colorN50 : Colors.colorN100
        }, 0.4)
        color: ramp(buckets($id,
        ['${this.selectedPOI}',
         '${this.selectedComparePOI}']),
        [hex('${Colors.colorAccent}'),
         hex('${Colors.colorSecondary}')])
        filter: or(
          eq($id, '${this.selectedPOI}')
          eq($id, '${this.selectedComparePOI}')
        )
      `
    }
  }

  getPoiViz() {
    let colors = this.poiLegendColors // .map(c => `opacity(${c}, 0.8)`);
    if (this.isLightBasemap()) {
      colors = colors.slice().reverse()
    }
    if (this.compareMode) {
      return this.getPoiCompareViz()
    }
    const poiMobilityViz = `
      ${this.getVizCommonVariables()}
      @ord: $order
      @features: viewportFeatures($id, @ntrips, $nvisitors, $nvisitors_other, $nvisits, $nvisits_other)
      @max_ntrips: viewportMax(@ntrips)
      @min_ntrips: viewportMin(@ntrips)
      @terrunit_src: $terrunit_src
      strokeWidth: 0.5
      strokeColor: opacity(${
        this.isLightBasemap() ? Colors.colorN50 : Colors.colorN100
      }, 0.4)
      color: ramp(
        linear(@ntrips, @min_ntrips, @max_ntrips),
        [${colors}]
      )
    `
    return poiMobilityViz
  }

  getPoiCompareViz() {
    if (this.selectedComparePOI) {
      return `
        ${this.getVizCommonVariables()}
        @terrunit_src: $terrunit_src
        @ord: $ord
        @ntrips_other: $${this.keyIndicator}_other
        @balance: (@ntrips_other - @ntrips) / @ntrips
        @features: viewportFeatures($id, @ntrips, @ntrips_other, $nvisitors, $nvisitors_other, $nvisits, $nvisits_other)
        strokeWidth: 0.5
        strokeColor: opacity(${
          this.isLightBasemap() ? Colors.colorN50 : Colors.colorN100
        }, 0.4)
        color: blend(
          blend(
            ramp(
              buckets(@balance, [${this.compareSteps.join(',')}]),
              [${this.legendCompareColorsByPois.join(',')}]
            ),
            ${
              this.legendCompareColorsByPois[
                this.legendCompareColorsByPois.length - 1
              ]
            },
            isNull(@ntrips)
          ),
          ${this.legendCompareColorsByPois[0]},
          isNull(@ntrips_other)
        )
      `
    } else {
      if (this.selectedComparePOI) {
        return `
          @name: $name
          @ntrips: $ntrips
          @ntrips_other: $ntrips_other
          @balance: ($ntrips_other - $ntrips) / $ntrips
          @terrunit_src: $terrunit_src
          @features: viewportFeatures($id, $ntrips, $ntrips_other)
          strokeWidth: 0.5
          strokeColor: opacity(${
            this.isLightBasemap() ? Colors.colorN50 : Colors.colorN100
          }, 0.4)
          color: ramp(
            buckets(@balance, [${this.compareSteps.join(',')}]),
            [${this.legendCompareColorsByPois.join(',')}]
          )
        `
      } else {
        return this.getVizCompareByDeliveries()
      }
    }
  }

  getVizCompareByDeliveries() {
    let keyIndicator = this.keyIndicator
    if (this.context.isDensityActive()) {
      keyIndicator += '_density'
    }
    let viz = `
      ${this.getVizCommonVariables()}
      @ntrips_other: $${keyIndicator}_other
      @balance: (@ntrips - @ntrips_other) / @ntrips
      strokeWidth: 0.5
      strokeColor: opacity(${
        this.isLightBasemap() ? Colors.colorN50 : Colors.colorN100
      }, 0.4)
      color: blend(
          blend(
            ramp(
              buckets(@balance, [${this.compareSteps.join(',')}]),
              [${this.legendCompareColorsByDeliverys.join(',')}]
            ),
            ${this.legendCompareColorsByDeliverys[0]},
            isNull(@ntrips)
          ),
          ${
            this.legendCompareColorsByDeliverys[
              this.legendCompareColorsByDeliverys.length - 1
            ]
          },
          isNull(@ntrips_other)
        )
    `
    if (this.selectedPOI) {
      viz += `
        @terrunit_src: $terrunit_src
        @features: viewportFeatures($id, @ntrips, @ntrips_other)
      `
    }
    return viz
  }

  togglePrintView($event: { fullMode: boolean } = { fullMode: true }) {
    this.reportFullMode = $event.fullMode
    this._showPrintView = !this._showPrintView
    if (this._showPrintView) {
      this.mapImage = this.map.getImage()
    } else {
      setTimeout(() => {
        this.map.invalidateSize()
      })
      this.titleService.setTitle('Vodafone Analytics')
    }
  }

  onOpenCompareSelector(value) {
    this.isActivedCompareSelector = value
  }

  isStatsEmpty() {
    if (!this.mobilityStats) {
      return
    }
    const stats = { ...this.mobilityStats }
    delete stats.legend
    return Object.values(stats)
      .filter((stat) => stat)
      .every((stat) =>
        Array.isArray(stat)
          ? stat.length === 0
          : Object.keys(stat).length === 0,
      )
  }

  isDownloadWidgetInactive() {
    return (
      !this.context.canShowReport() &&
      !this.context.canShowCartoDownload() &&
      !this.context.canShowCSVDownload()
    )
  }
}
